import { useEffect, useRef, useState } from 'react';
import useLineLiff from 'hooks/LineLiff';
import useDragScroll from '../../../hooks/useDragScroll';
import { uploadToyotaPremiumWallpaper } from '../../lexusOfficialAccount/utils/LinePostbackActions';
import {
  getModelColors,
  getModelImage,
  useWallpaperSettings,
  getBackgroundImage,
} from './modelOptions';
import morningMist from '../../../assets/toyotaPremiun/morning-mist.jpg';
import urbanOasis from '../../../assets/toyotaPremiun/urban-oasis.jpg';
import cityNightscape from '../../../assets/toyotaPremiun/city-nightscape.jpg';
import coastalMorning from '../../../assets/toyotaPremiun/coastal-morning.jpg';
import sunDune from '../../../assets/toyotaPremiun/sun-dune.jpg';
import spinner from '../../../assets/toyotaPremiun/spinner.svg';

// 選項內容
const modelScenes = [
  { name: '晨霧天際', img: morningMist, background: 'gray' },
  { name: '城市綠洲', img: urbanOasis, background: 'city' },
  { name: '都會夜景', img: cityNightscape, background: 'dark' },
  { name: '海岸晨光', img: coastalMorning, background: 'beach' },
  { name: '暖陽沙丘', img: sunDune, background: 'dune' },
];

const textColors = [
  { color: '#78613E' },
  { color: '#C2C6CB' },
  { color: '#1A1A1C' },
  { color: '#F2F0EA' },
  { color: '#873027' },
];

// svg
const DownloadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 10.167V1.50049"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M14.5 9.5V12.1667C14.5 12.5203 14.3478 12.8594 14.0769 13.1095C13.806 13.3595 13.4386 13.5 13.0556 13.5H2.94444C2.56135 13.5 2.19395 13.3595 1.92307 13.1095C1.65218 12.8594 1.5 12.5203 1.5 12.1667V9.5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />

    <path
      d="M4.38867 6.55566L7.99973 10.1667L11.6108 6.55566"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GoToIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none">
    <path
      d="M5.73372 12.8L10.4004 8.13338L5.73372 3.46672"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const GlowCircle = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="100"
    height="100"
    viewBox="0 0 100 100"
    fill="none">
    <g opacity="0.9" filter="url(#filter0_f_4996_1447)">
      <circle cx="50" cy="38" r="22" fill="#A59B80" />
    </g>
    <defs>
      <filter
        id="filter0_f_4996_1447"
        x="-23.1345"
        y="-35.1345"
        width="146.269"
        height="146.269"
        filterUnits="userSpaceOnUse"
        colorInterpolationFilters="sRGB">
        <feFlood floodOpacity="0" result="BackgroundImageFix" />
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="BackgroundImageFix"
          result="shape"
        />
        <feGaussianBlur
          stdDeviation="25.5673"
          result="effect1_foregroundBlur_4996_1447"
        />
      </filter>
    </defs>
  </svg>
);

function CustomWallpaper() {
  const { liff, userInfo } = useLineLiff(
    process.env.REACT_APP_LIFF_CUSTOM_WALLPAPER
  );
  const { modelTypes, isReady } = useWallpaperSettings();

  const previewRef = useRef(null);
  const previewImageRef = useRef(null);
  const modelListScroll = useDragScroll();
  const modelColorListScroll = useDragScroll();
  const sceneListScroll = useDragScroll();
  const textColorListScroll = useDragScroll();
  const [generateStatus, setGenerateStatus] = useState('default');
  const [showSavedToast, setShowSavedToast] = useState(false);
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);
  const [showLoadingToast, setShowLoadingToast] = useState(false);
  const [hasGeneratedWallpaper, setHasGeneratedWallpaper] = useState(false);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedType, setSelectedType] = useState('CROWN SPORT');
  const [selectedScene, setSelectedScene] = useState('晨霧天際');
  const [selectedTextColor, setSelectedTextColor] = useState('#78613E');
  const [signature, setSignature] = useState('');
  const selectedSceneBackground = getBackgroundImage(
    modelScenes.find((scene) => scene.name === selectedScene)?.background ??
      'gray'
  );
  const selectedModelColors = getModelColors(selectedType);
  const selectedModelImage = getModelImage(selectedType, selectedColor);
  const downloadFilelRef = useRef('');
  const [backgroundLoading, setBackgroundLoading] = useState(true);
  const [carLoading, setCarLoading] = useState(true);
  const [carLink, setCarLink] = useState('https://toyota.tw/JCdsN0');

  // 取得桌布相關資訊
  useEffect(() => {
    if (!isReady || !modelTypes.length) return;

    const firstModel = modelTypes[0];

    setSelectedType(firstModel.name);
    setSelectedColor(firstModel.colors?.[0]?.name ?? '');
  }, [isReady, modelTypes]);

  const handleModelTypeChange = (model) => {
    setSelectedType(model.name);
    setSelectedColor(model.colors?.[0]?.name ?? '');
    setCarLink(model.link);
  };

  // 頁面滑動
  useEffect(() => {
    if (generateStatus === 'loading') {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [generateStatus, showSavedToast]);

  const scrollToPreview = () => {
    previewRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  // 點擊「生成我的桌布」：合成圖片並上傳到後端取得下載網址
  const handleGenerateBtnClick = async () => {
    if (!previewImageRef.current) return;

    try {
      const idToken = liff.getIDToken();
      const accessToken = liff.getAccessToken();

      scrollToPreview();
      setGenerateStatus('loading');
      setShowLoadingOverlay(true);

      const tokenToSend = idToken || accessToken;
      if (!tokenToSend) {
        throw new Error('無法取得使用者驗證資訊，請重新登入 LINE');
      }

      const payload = {
        car_type: selectedType,
        car_color: selectedColor,
        background: selectedScene,
        text: signature,
        text_color: selectedTextColor,
      };

      const response = await uploadToyotaPremiumWallpaper(tokenToSend, payload);

      // 第 3 步：後端回傳 file_name，存起來
      if (!response?.success || !response?.data?.file_name) {
        throw new Error(response?.message || '上傳桌布失敗');
      }

      downloadFilelRef.current = response.data.file_name;

      // 第 4 步：設為成功狀態，顯示提示
      setHasGeneratedWallpaper(true);
      setGenerateStatus('success');
      setShowLoadingOverlay(false);
      setShowLoadingToast(true);
      setTimeout(() => {
        setShowLoadingToast(false);
        setGenerateStatus('default');
      }, 1000);
    } catch (error) {
      console.error('產生桌布失敗：', error);
      setShowLoadingOverlay(false);
      setGenerateStatus('default');
      alert(error.message || '產生桌布失敗，請稍後再試');
    }
  };

  // 點擊「收藏桌布」：使用已儲存的 download_url 進行下載
  const handleSaveBtnClick = () => {
    if (!downloadFilelRef.current) return;

    const downloadUrl = `${process.env.REACT_APP_BASE_URL}/custom_wallpaper/download?file_name=${downloadFilelRef.current}`;

    try {
      if (liff?.isInClient?.()) {
        liff.openWindow({ url: downloadUrl, external: true });
      } else {
        window.open(downloadUrl, '_blank');
      }

      setShowSavedToast(true);
      setTimeout(() => {
        setShowSavedToast(false);
      }, 1500);
    } catch (error) {
      console.error('下載桌布失敗：', error);
      alert('下載桌布失敗，請稍後再試');
    }
  };

  return (
    <div className="custom-wallpaper">
      {generateStatus === 'loading' && (
        <div className="page-loading-overlay"></div>
      )}
      <div className="preview-section" ref={previewRef}>
        <div className="preview-section__description">
          <p className="preview-section__description-text">
            請選擇偏好的車款製作
            <br />
            TOYOTA PREMIUM 專屬桌布
          </p>
        </div>
        <div className="preview-section__image-area mt20">
          <div className="preview-image-container" ref={previewImageRef}>
            {(carLoading || backgroundLoading) && (
              <div className="preview-image-loading-overlay">
                <img src={spinner} alt="loading" />
                <span>載入中...</span>
              </div>
            )}
            <img
              className="preview-image"
              src={selectedSceneBackground}
              alt={`${selectedScene}桌布預覽`}
              onLoad={() => setBackgroundLoading(false)}
            />
            {signature && (
              <span
                className="preview-signature"
                style={{ color: selectedTextColor }}>
                {signature}
              </span>
            )}
            <img
              className="preview-car-image"
              src={selectedModelImage}
              alt={selectedType}
              onLoad={() => setCarLoading(false)}
            />
            {showLoadingOverlay && (
              <div className="preview-image-loading-overlay">
                <div className="preview-image-loading-overlay__spinner">
                  <img
                    className="preview-image-loading-overlay__spinner-img"
                    src={spinner}
                    alt="loading"
                  />
                </div>
                <span className="preview-image-loading-overlay__text">
                  畫面生成中
                </span>
              </div>
            )}
          </div>
          {showLoadingToast && (
            <div className="preview-section__image-area-toast-overlay">
              <div className="preview-section__image-area-toast">
                <p className="preview-section__image-area-toast-text">
                  桌布製作完成
                </p>
              </div>
            </div>
          )}
          {showSavedToast && (
            <div className="preview-section__image-area-toast-overlay">
              <div className="preview-section__image-area-toast">
                <p className="preview-section__image-area-toast-text">
                  已儲存桌布
                </p>
              </div>
            </div>
          )}
        </div>
        {hasGeneratedWallpaper && (
          <div className="preview-section__actions mt20">
            <button
              className="preview-section__button"
              onClick={handleSaveBtnClick}>
              <span className="preview-section__button-text">收藏桌布</span>
              <DownloadIcon />
            </button>
            <a
              className="preview-section__button"
              href={carLink}
              target="_blank"
              rel="noopener noreferrer">
              <span className="preview-section__button-text">了解更多</span>
              <GoToIcon />
            </a>
          </div>
        )}
      </div>
      <div className="customize-section mt20">
        <div className="customize-option">
          <div className="customize-option__header">
            <span className="customize-option__number">1.</span>
            <p className="customize-option__title">選擇車款</p>
          </div>
          <div
            className="model-list-container"
            ref={modelListScroll.ref}
            {...modelListScroll.dragEvents}>
            <div className="model-list">
              {modelTypes.map((type) => (
                <div
                  className={`model-item ${
                    selectedType === type.name ? 'model-item--active' : ''
                  }`}
                  key={type.name}
                  onClick={() => handleModelTypeChange(type)}>
                  <div className="model-item__glow">
                    <GlowCircle />
                  </div>
                  <div className="model-item__img-container">
                    <img
                      className="model-item__img"
                      src={type.img}
                      alt={type.name}
                    />
                  </div>
                  <span className="model-item__name">{type.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="customize-option">
          <div className="customize-option__header">
            <span className="customize-option__number">2.</span>
            <p className="customize-option__title">車款顏色</p>
          </div>
          <div
            className="customize-option__color-list-container"
            ref={modelColorListScroll.ref}
            {...modelColorListScroll.dragEvents}>
            <div className="customize-option__color-list">
              {selectedModelColors.map((color) => (
                <div
                  className={`customize-option__color-item ${
                    selectedColor === color.name
                      ? 'customize-option__color-item--active'
                      : ''
                  }`}
                  key={color.name}
                  onClick={() => setSelectedColor(color.name)}>
                  <div
                    className="customize-option__color-swatch"
                    style={{ backgroundColor: color.color }}></div>
                  <span className="customize-option__color-name">
                    {color.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="customize-option">
          <div className="customize-option__header">
            <span className="customize-option__number">3.</span>
            <p className="customize-option__title">場景</p>
          </div>
          <div
            className="scene-list-container"
            ref={sceneListScroll.ref}
            {...sceneListScroll.dragEvents}>
            <div className="scene-list">
              {modelScenes.map((scene) => (
                <div
                  className={`scene-item ${
                    selectedScene === scene.name ? 'scene-item--active' : ''
                  }`}
                  key={scene.name}
                  onClick={() => setSelectedScene(scene.name)}
                  style={{ backgroundImage: `url(${scene.img})` }}>
                  <span className="scene-item__name">{scene.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="customize-option">
          <div className="customize-option__header">
            <span className="customize-option__number">4.</span>
            <p className="customize-option__title">字體配色</p>
          </div>
          <div
            className="customize-option__color-list-container"
            ref={textColorListScroll.ref}
            {...textColorListScroll.dragEvents}>
            <div className="customize-option__color-list">
              {textColors.map((textColor) => (
                <div
                  className={`customize-option__color-item ${
                    selectedTextColor === textColor.color
                      ? 'customize-option__color-item--active'
                      : ''
                  }`}
                  key={textColor.color}
                  onClick={() => setSelectedTextColor(textColor.color)}>
                  <div
                    className="customize-option__color-swatch"
                    style={{ backgroundColor: textColor.color }}></div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="customize-option">
          <div className="customize-option__header">
            <span className="customize-option__number">5.</span>
            <p className="customize-option__title">個人化簽名</p>
          </div>
          <div className="customize-option__input-container">
            <input
              className="customize-option__input"
              type="text"
              placeholder="請輸入簽名（最多 20 字）"
              value={signature}
              maxLength={20}
              onChange={(event) => setSignature(event.target.value)}
            />
            <span className="customize-option__character-count">
              {signature.length}/20
            </span>
            <p className="customize-option__notice">
              此為示意圖，請以實車外觀為準
            </p>
          </div>
        </div>
      </div>

      <div className="fixed-button-container">
        <button
          className={`
          generate-button 
          generate-button--${generateStatus}`}
          onClick={handleGenerateBtnClick}>
          {generateStatus === 'loading' && '生成中'}
          {generateStatus === 'success' && '生成我的桌布'}
          {generateStatus === 'default' && '生成我的桌布'}
        </button>
      </div>
    </div>
  );
}

export default CustomWallpaper;
